import React, { useState } from "react";
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

import { formatSurgeryStageLabel } from "./surgeryStageOptions";

type PatientImageItemData = {
  image_data?: {
    id?: string | number | null;
    view_type_human_readable?: string | null;
    surgery_stage?: string | null;
  } | null;
  presigned_urls?: string[] | null;
  thumbnail_presigned_urls?: string[] | null;
  resolved_image_url?: string | null;
};

type PatientImageItemProps = {
  item: PatientImageItemData;
  index: number;
  isEditing?: boolean;
  isSelected?: boolean;
  isUpdatingStage?: boolean;
  onImageTap?: (item: PatientImageItemData) => void;
  onSelectionToggle: (item: PatientImageItemData) => void;
};

const tokens = {
  color: {
    surface: "#FFFFFF",
    border: "#EEEEEE",
    text: "#333333",
    textMuted: "#777777",
    icon: "#555555",
    overlay: "rgba(0, 0, 0, 0.28)",
    focus: "#00B7F4",
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
  },
  radius: {
    md: 8,
  },
  fontSize: {
    xs: 12,
    sm: 14,
  },
};

const PatientImageItem = ({
  item,
  index,
  isEditing = false,
  isSelected = false,
  isUpdatingStage = false,
  onImageTap,
  onSelectionToggle,
}: PatientImageItemProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const metadata = item.image_data || {};
  const imageUrl = item.presigned_urls?.[0];
  const thumbnailUrl = item.thumbnail_presigned_urls?.[0] || imageUrl;
  const resolvedImageUrl = item.resolved_image_url || null;
  const displayImageUrl = resolvedImageUrl || thumbnailUrl || imageUrl;
  const viewType = metadata.view_type_human_readable;
  const currentStage = metadata.surgery_stage;

  return (
    <View style={styles.imageContainer}>
      <TouchableOpacity
        style={[
          styles.imageTapTarget,
          isSelected && styles.selectedImageTapTarget,
          isFocused && styles.focusedControl,
          isUpdatingStage && styles.disabledControl,
        ]}
        onPress={() => {
          if (isEditing) {
            onSelectionToggle(item);
          } else {
            onImageTap && onImageTap(item);
          }
        }}
        disabled={isUpdatingStage}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        testID={`consult-photo-item-${metadata.id || "unknown"}`}
        accessibilityRole="button"
        accessibilityLabel={
          isEditing
            ? `${isSelected ? "Deselect" : "Select"} patient photo ${index + 1}`
            : `Open patient photo ${index + 1}`
        }
        accessibilityState={{
          selected: isEditing ? isSelected : undefined,
          disabled: isUpdatingStage,
        }}
      >
        <Image
          source={{ uri: displayImageUrl || "" }}
          style={styles.image}
          resizeMode="cover"
        />
        {isEditing && isSelected ? (
          <View style={styles.selectionOverlay}>
            {isUpdatingStage ? (
              <ActivityIndicator size="small" color={tokens.color.surface} />
            ) : (
              <MaterialIcons
                name="check"
                size={24}
                color={tokens.color.surface}
              />
            )}
          </View>
        ) : null}
      </TouchableOpacity>

      <Text style={styles.imageCaption}>
        {viewType
          ? viewType
          : currentStage
            ? formatSurgeryStageLabel(currentStage)
            : ""}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  imageContainer: {
    width: 120,
    minHeight: 148,
    position: "relative",
  },
  imageTapTarget: {
    width: 120,
    height: 120,
    borderRadius: tokens.radius.md,
  },
  image: {
    width: 120,
    height: 120,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: tokens.color.border,
  },
  selectedImageTapTarget: {
    borderWidth: 2,
    borderColor: tokens.color.focus,
  },
  focusedControl: {
    borderWidth: 2,
    borderColor: tokens.color.focus,
  },
  selectionOverlay: {
    position: "absolute",
    top: tokens.spacing.xs,
    right: tokens.spacing.xs,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: tokens.color.overlay,
    alignItems: "center",
    justifyContent: "center",
  },
  disabledControl: {
    opacity: 0.6,
  },
  imageCaption: {
    fontSize: tokens.fontSize.xs,
    color: tokens.color.textMuted,
    marginTop: tokens.spacing.xs,
    textAlign: "center",
  },
});

export default PatientImageItem;
